import { useId, useRef, useState, type ReactNode } from 'react';
import type { RoomDocument } from '../api/types';
import { useEscapeToClose } from '../lib/dialog-stack';
import { useDialogFocus } from '../lib/focus-trap';
import { cn } from '../lib/utils';
import { useDocumentPreview } from '../lib/use-document-preview';
import {
  redactedFilename,
  useRedactedCopy,
  viewerPlan,
  type RedactedCopyState,
  type ViewerNotice,
} from '../lib/use-buyer-copy';
import { filingSummary } from '../lib/document-filing';
import { Button, buttonVariants } from './button';
import { DocumentFileNotes, type DataRoomAudience } from './data-room-list';
import { DocumentPreviewView } from './document-preview';
import { PdfViewer } from './pdf-viewer';

export interface DocumentViewerModalProps {
  /** The room the document belongs to — used to build the content endpoint. */
  roomId: string;
  /** The document being previewed. */
  document: RoomDocument;
  onClose: () => void;
  /**
   * Opens the redaction review for this document. When given, a held document
   * whose redaction needs a person (needs review / unsupported / failed) shows
   * a "Review redaction" button. Omit it where the viewer cannot review.
   */
  onReviewRedaction?: (document: RoomDocument) => void;
  /** "Manage document" (re-file it, confirm its date): when given, the viewer
   * offers it and renders this as its dialog — the room admin's pages, where
   * re-filing is already offered. Omit it where the viewer may not re-file. */
  renderManage?: (onClose: () => void) => ReactNode;
  /**
   * Start on the ORIGINAL file instead of the copy buyers see — "Open the file"
   * on a file we couldn't read (ADR-0051 update 2026-10-15): the admin and the
   * seller must be able to look at it to judge or cure it. Buyers never get
   * here; their copy is unchanged.
   */
  original?: boolean;
  /** Whose notes these are (the seller is the one asked for a copy). Default admin. */
  audience?: DataRoomAudience;
  /**
   * What the reader can do about a file we couldn't read — the app's
   * `UnreadableFileOptions`, shown under the "Couldn't read" note. `openOriginal`
   * switches the viewer to the original file (undefined once it shows it).
   */
  renderUnreadableOptions?: (openOriginal: (() => void) | undefined) => ReactNode;
  /** A "?" help tip shown after the "Redacted — the copy buyers see" caption. */
  redactedCaptionAside?: ReactNode;
}

/**
 * In-app document viewer for the data room — the room admin's and the bound
 * seller's (owner, 2026-10-15: the seller's document area looks just like the
 * admin's) (owner ask: "I cannot view the file
 * in the site, so I don't know which tax return it is"). Opened from the
 * per-document detail modal, it fetches the document bytes from the same-origin,
 * cookie-authenticated content endpoint
 * (GET /api/v1/rooms/:roomId/documents/:documentId/content) and renders them
 * inline by Content-Type so the admin can actually SEE the file and verify both
 * its contents and its classification.
 *
 * The bytes are BINARY, so this uses the raw credentialed `fetch` (matching the
 * shared api client's `credentials: 'include'` + `/api/v1` base) and reads a
 * Blob → object URL, rather than the JSON api client. The object URL is revoked
 * on unmount / when the viewed document changes so no blob leaks.
 *
 * Unlike the buyer-facing room viewer this is NOT view-only: the admin owns the
 * room, so a Download button is offered for every file type (and is the only
 * affordance for types we cannot preview at all — zip, …). Spreadsheets, Word,
 * CSV and other office files render from the server-built preview (ADR-0033) —
 * a sheet grid with tabs, Word HTML or text. PDFs use
 * the same pdf.js canvas viewer as the buyer room (ADR-0027), so the only
 * download/print path is our own button, never the browser plugin's toolbar.
 *
 * It shows what BUYERS see (owner, 2026-10-15: "Everyone who is viewing the
 * file in the data room should see the same redactions"): the redacted copy
 * once redaction is complete, "Being prepared" while it is not, and the
 * original only where buyers get the original (see `viewerPlan`). Download
 * gives whatever is shown.
 */
export function DocumentViewerModal({
  roomId,
  document,
  onClose,
  onReviewRedaction,
  renderManage,
  original = false,
  audience = 'admin',
  renderUnreadableOptions,
  redactedCaptionAside,
}: DocumentViewerModalProps) {
  // "Manage document" (owner ask 2026-10-15): re-file and confirm the date from
  // the top of the viewer. Deal paperwork is never filed, so it has none.
  const manageable = Boolean(renderManage) && document.aiClassifiable !== false;
  const [managing, setManaging] = useState(false);
  // The original file, on request (a file we couldn't read): never the buyer view.
  const [originalFile, setOriginalFile] = useState(original);
  const plan: ReturnType<typeof viewerPlan> = originalFile
    ? { kind: 'original' }
    : viewerPlan(document);
  const openOriginal = originalFile ? undefined : () => setOriginalFile(true);
  const redacted = useRedactedCopy(roomId, document.id, plan.kind === 'redacted');
  // Complete with no copy = nothing needed hiding: buyers get the original.
  // An unpacked zip / saved email is never redacted (its files are): the admin and
  // the seller open the container itself (review F2).
  const showOriginal =
    plan.kind === 'original' ||
    plan.kind === 'container' ||
    (plan.kind === 'redacted' && redacted.kind === 'none');
  // A redacted Word / Excel copy is shown as buyers see it: the server's preview
  // of that copy (GET …/preview?copy=buyer — a sheet grid, formatted text),
  // never the original's.
  const redactedOffice = plan.kind === 'redacted' && redacted.kind === 'file';
  const showRedacted = plan.kind === 'redacted' && redacted.kind !== 'none' && !redactedOffice;
  const { content, error, loading } = useDocumentPreview(
    roomId,
    document.id,
    document.name,
    showOriginal || redactedOffice,
    redactedOffice,
  );
  // Download gives whatever is shown: the redacted copy's own file whenever a
  // copy exists, the original only where buyers get the original.
  const download =
    showRedacted || redactedOffice
      ? redacted.kind === 'pdf' || redacted.kind === 'file' || redacted.kind === 'image'
        ? {
            url: redacted.url,
            name: redactedFilename(
              document.name,
              redacted.kind === 'pdf' ? 'pdf' : redacted.extension,
            ),
          }
        : null
      : showOriginal && content?.url
        ? { url: content.url, name: document.name }
        : null;

  // Close on Escape for keyboard users — only while this is the topmost dialog
  // (it opens over another dialog, which must stay open on the same key press).
  useEscapeToClose(onClose);
  // Focus moves into the viewer, Tab stays inside, and closing hands focus
  // back to whatever opened it (the document name or the ⋯ menu).
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogFocus(dialogRef);

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      // Sits above the detail modal (z-50) that opened it.
      className="fixed inset-0 z-[60] flex flex-col bg-scrim p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-4 py-3">
          {/* A full row of its own on a phone; the controls wrap below it. */}
          <div className="min-w-0 grow basis-full sm:basis-0">
            <div className="flex min-w-0 items-center gap-2">
              <h2 id={titleId} className="min-w-0 truncate text-sm font-semibold">
                <span className="sr-only">Viewing </span>
                {document.name}
              </h2>
              {manageable ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setManaging(true)}
                >
                  Manage document
                </Button>
              ) : null}
            </div>
            {/* Where it is filed and under what date, at a glance. */}
            <p
              className="truncate text-xs text-muted-foreground"
              data-testid="viewer-filing"
              title={filingSummary(document)}
            >
              <span className="sr-only">Filed under: </span>
              {filingSummary(document)}
            </p>
            {plan.kind === 'container' ? (
              <p className="truncate text-xs text-muted-foreground" data-testid="container-note">
                {plan.note}
              </p>
            ) : null}
            {originalFile ? (
              <p className="truncate text-xs text-muted-foreground" data-testid="original-caption">
                The original file — not the copy buyers see
              </p>
            ) : null}
            {document.unreadable ? (
              <div data-testid="viewer-unreadable">
                <DocumentFileNotes
                  document={{ unreadable: document.unreadable }}
                  audience={audience}
                  unreadableOptions={renderUnreadableOptions?.(openOriginal)}
                />
              </div>
            ) : null}
            {redacted.kind === 'pdf' || redacted.kind === 'file' || redacted.kind === 'image' ? (
              <div className="flex items-center gap-1.5">
                <p
                  className="truncate text-xs text-muted-foreground"
                  data-testid="redacted-caption"
                >
                  Redacted — the copy buyers see
                </p>
                {redactedCaptionAside}
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {download ? (
              <a
                href={download.url}
                download={download.name}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
              >
                Download
              </a>
            ) : null}
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close viewer">
              Close
            </Button>
          </div>
        </div>

        {plan.kind === 'held' ? (
          <HeldView
            notice={plan.notice}
            onReview={onReviewRedaction ? () => onReviewRedaction(document) : undefined}
            onOpenOriginal={document.unreadable ? openOriginal : undefined}
          />
        ) : showRedacted ? (
          <RedactedView state={redacted} title={document.name} />
        ) : content?.kind === 'pdf' && content.data ? (
          <PdfViewer
            data={content.data}
            title={document.name}
            errorHint={`Use the Download button above to open “${document.name}” on your device.`}
            className="min-h-0 flex-1"
          />
        ) : content?.kind === 'office' &&
          content.preview &&
          content.preview.kind !== 'unsupported' ? (
          <DocumentPreviewView
            preview={content.preview}
            title={document.name}
            className="min-h-0 flex-1"
          />
        ) : (
          <div className="relative flex-1 overflow-auto bg-muted/30">
            <div className="flex min-h-full flex-col items-center justify-center p-4">
              {loading ? (
                <div className="flex flex-col items-center gap-3 text-muted-foreground">
                  <span
                    aria-hidden="true"
                    className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
                  />
                  <p className="text-sm">Loading document…</p>
                </div>
              ) : error ? (
                <p className="max-w-md text-center text-sm text-destructive">{error}</p>
              ) : content?.kind === 'image' ? (
                <img
                  src={content.url}
                  alt={document.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : content?.kind === 'text' ? (
                <pre className="h-full w-full self-stretch overflow-auto whitespace-pre-wrap break-words rounded border bg-background p-4 text-left text-xs leading-relaxed">
                  {content.text}
                </pre>
              ) : content ? (
                <div className="max-w-md text-center">
                  <p className="text-sm font-medium">
                    {content.preview?.kind === 'unsupported' &&
                    content.preview.reason === 'unreadable'
                      ? 'This file couldn’t be read for a preview.'
                      : 'Preview not available for this file type.'}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Use the Download button above to open “{document.name}” on your device.
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {managing && renderManage ? (
        // Its scrim click must not reach the viewer's own (which closes the viewer).
        <div onClick={(e) => e.stopPropagation()}>{renderManage(() => setManaging(false))}</div>
      ) : null}
    </div>
  );
}

/** The redacted PDF or picture in the same viewer (a Word / Excel / TIFF copy is shown through its preview). */
function RedactedView({ state, title }: { state: RedactedCopyState; title: string }) {
  if (state.kind === 'image') {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/30 p-4">
        <img src={state.url} alt={`${title} (redacted)`} className="max-h-full max-w-full" />
      </div>
    );
  }
  if (state.kind === 'pdf') {
    return (
      <PdfViewer
        data={state.data}
        title={`${title} (redacted)`}
        errorHint="Use the Download button above to open the redacted copy on your device."
        className="min-h-0 flex-1"
      />
    );
  }
  return (
    <div className="relative flex-1 overflow-auto bg-muted/30">
      <div className="flex min-h-full flex-col items-center justify-center p-4">
        {state.kind === 'error' ? (
          <p className="max-w-md text-center text-sm text-destructive">{state.message}</p>
        ) : state.kind === 'file' ? (
          <div className="max-w-md text-center">
            <p className="text-sm font-medium">
              The redacted {state.extension === 'xlsx' ? 'spreadsheet' : 'Word document'} can’t be
              shown here.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Use the Download button above to open it on your device.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <span
              aria-hidden="true"
              className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
            />
            <p className="text-sm">Loading document…</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Nothing to show yet — what buyers see ("Being prepared") and why. */
function HeldView({
  notice,
  onReview,
  onOpenOriginal,
}: {
  notice: ViewerNotice;
  onReview?: () => void;
  /** A file we couldn't read: look at the original to judge it. */
  onOpenOriginal?: () => void;
}) {
  return (
    <div className="relative flex-1 overflow-auto bg-muted/30">
      <div className="flex min-h-full flex-col items-center justify-center p-4">
        <div className="max-w-md text-center" data-testid="viewer-notice" data-tone={notice.tone}>
          <p className={cn('text-sm font-medium', notice.tone === 'check' && 'text-st-check-fg')}>
            {notice.title}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{notice.detail}</p>
          {notice.reviewable && onReview ? (
            <Button variant="outline" size="sm" className="mt-3" onClick={onReview}>
              Review redaction
            </Button>
          ) : null}
          {onOpenOriginal ? (
            <Button variant="outline" size="sm" className="ml-2 mt-3" onClick={onOpenOriginal}>
              Open the original file
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
